import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function TermsOfService() {
  const { t } = useTranslation();

  // Helper to get array translations
  const getList = (key) => t(key, { returnObjects: true }) || [];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div {...fadeUp} className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-6">
            <FileText className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {t('legal.terms.title')}
          </h1>
          <p className="text-slate-500">
            {t('legal.lastUpdated')} : {t('legal.toComplete')}
          </p>
        </motion.div>

        <motion.div 
          {...fadeUp}
          className="bg-white rounded-2xl shadow-soft p-8 md:p-12 space-y-10"
        >
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section1.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section1.p1')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section1.p2')}
            </p>
            <p className="text-slate-600 leading-relaxed">
              {t('legal.terms.section1.p3')}
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section2.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section2.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.terms.section2.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.terms.section2.note')}
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section3.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section3.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.terms.section3.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section4.title')}
            </h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.terms.section4.pricing')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section4.pricingText')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.terms.section4.payment')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section4.paymentText')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.terms.section4.renewal')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section4.renewalText')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.terms.section4.refunds')}</h3>
            <p className="text-slate-600 leading-relaxed">
              {t('legal.terms.section4.refundsText')}
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section5.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section5.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.terms.section5.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.terms.section5.note')}
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section6.title')}
            </h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.terms.section6.management')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section6.managementText')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.terms.section6.deletion')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section6.deletionIntro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.terms.section6.deletionList').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.terms.section6.deletionNote')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2 mt-4">{t('legal.terms.section6.expiration')}</h3>
            <p className="text-slate-600 leading-relaxed">
              {t('legal.terms.section6.expirationText')}
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section7.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section7.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.terms.section7.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.terms.section7.note')}
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section8.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section8.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.terms.section8.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.terms.section8.note')}
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section9.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section9.p1')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section9.p2')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.terms.section9.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.terms.section9.p3')}
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section10.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section10.p1')}
            </p>
            <p className="text-slate-600 leading-relaxed">
              {t('legal.terms.section10.p2')}
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.terms.section11.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.terms.section11.intro')}
            </p>
            <div className="bg-slate-50 rounded-xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t('legal.terms.section11.department')}</p>
                <a href="mailto:hello@ikatone.com" className="text-indigo-600 hover:underline">
                  hello@ikatone.com
                </a>
              </div>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
